import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import * as api from "../../api/index.js";
import { IoMdArrowBack } from "react-icons/io";
import { FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiChevronRight } from "react-icons/fi";
import AOS from "aos";
import "aos/dist/aos.css";

const Auth = ({ auth, setAuth, refreshAuth }) => {
  const [formData, setFormData] = useState({
    Name: "",
    Email: "",
    Password: "",
    typeofuser: "Customer",
  });

  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setLoading(true);

    try {
      let response;
      const formPayload = { ...formData, Email: formData.Email.toLowerCase() };

      if (auth) {
        // ✅ LOGIN LOGIC
        response = await api.login(formPayload);
        if (response.data?.token) {
          localStorage.setItem("token", response.data.token);
          if (refreshAuth) refreshAuth();
        }
        navigate("/");
      } else {
        // ✅ SIGNUP LOGIC
        response = await api.signup(formPayload);
        const userType = formPayload.typeofuser;

        localStorage.setItem("userEmail", formPayload.Email);

        // Logic-based Navigation
        if (userType === "Seller") {
          navigate("/complete-profile-seller", { state: { email: formPayload.Email, typeofuser: userType } });
        } else if (userType === "Rider") {
          navigate("/complete-rider-page", { state: { email: formPayload.Email, typeofuser: userType } });
        } else {
          navigate("/complete-profile", { state: { email: formPayload.Email, typeofuser: userType } });
        }
      }
    } catch (error) {
      setErrorMessage(error?.response?.data?.error || error.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col relative overflow-hidden">
      {/* 🟠 Decorative Background Element */}
      <div className="absolute top-[-10%] right-[-10%] w-64 h-64 bg-orange-100 rounded-full blur-3xl opacity-50"></div>
      <div className="absolute bottom-[-5%] left-[-5%] w-64 h-64 bg-blue-50 rounded-full blur-3xl opacity-50"></div>

      {/* Header / Back Navigation */}
      <div className="p-6 relative z-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-gray-900 group"
        >
          <div className="p-2 bg-white rounded-full shadow-sm group-hover:bg-orange-50 transition-colors">
            <IoMdArrowBack size={20} />
          </div>
          <span className="text-xs font-black uppercase tracking-widest">Go Back</span>
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 relative z-10" data-aos="fade-up">
        <div className="w-full max-w-md bg-white rounded-[2.5rem] p-8 shadow-2xl shadow-gray-200/50 border border-gray-50">
          
          {/* Custom Tabs */}
          <div className="flex bg-gray-100 p-1.5 rounded-2xl mb-8">
            <button
              onClick={() => setAuth(false)}
              className={`flex-1 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${
                !auth ? "bg-white text-orange-600 shadow-sm" : "text-gray-400"
              }`}
            >
              Join Us
            </button>
            <button
              onClick={() => setAuth(true)}
              className={`flex-1 py-3 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${
                auth ? "bg-white text-orange-600 shadow-sm" : "text-gray-400"
              }`}
            >
              Sign In
            </button>
          </div>

          <div className="mb-8">
            <h1 className="text-3xl font-black tracking-tighter text-gray-900 mb-2">
              {auth ? "Welcome Back" : "Create Account"}
            </h1>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">
              {auth ? "Access your personalized dashboard" : "Join the OurASAP ecosystem today"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {!auth && (
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Full Name</label>
                <div className="relative">
                  <FiUser className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    name="Name"
                    placeholder="Name"
                    value={formData.Name}
                    onChange={handleChange}
                    className="w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 focus:ring-0 transition-all text-sm font-bold"
                    required
                  />
                </div>
              </div>
            )}

            <div className="space-y-1">
              <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Email Address</label>
              <div className="relative">
                <FiMail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="email"
                  name="Email"
                  placeholder="Email"
                  value={formData.Email}
                  onChange={handleChange}
                  className="w-full pl-12 pr-4 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 focus:ring-0 transition-all text-sm font-bold"
                  required
                />
              </div>
            </div>

            {!auth && (
              <div className="space-y-1">
                <label className="text-[10px] font-black text-gray-400 uppercase ml-1">Account Type</label>
                <select
                  name="typeofuser"
                  value={formData.typeofuser}
                  onChange={handleChange}
                  className="w-full px-4 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 focus:ring-0 transition-all text-sm font-bold appearance-none cursor-pointer"
                  required
                >
                  <option value="Customer">🛍️ Buyer</option>
                  <option value="Seller">🏬 Seller</option>
                  {/* <option value="Rider">🛵 Rider</option> */}
                </select>
              </div>
            )}

            <div className="space-y-1">
              <div className="flex justify-between items-center px-1">
                <label className="text-[10px] font-black text-gray-400 uppercase">Password</label>
                {auth && (
                  <span
                    onClick={() => navigate("/generate-reset")}
                    className="text-[10px] font-black text-orange-600 uppercase cursor-pointer hover:underline"
                  >
                    Forgot?
                  </span>
                )}
              </div>
              <div className="relative">
                <FiLock className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  name="Password"
                  placeholder="••••••••"
                  value={formData.Password}
                  onChange={handleChange}
                  className="w-full pl-12 pr-12 py-4 bg-gray-50 border-transparent rounded-2xl focus:bg-white focus:border-orange-500 focus:ring-0 transition-all text-sm font-bold"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-500 transition-colors"
                >
                  {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gray-900 text-white rounded-2xl font-black text-xs uppercase tracking-[0.2em] shadow-xl shadow-gray-200 hover:bg-black transition-all active:scale-95 disabled:opacity-70 flex items-center justify-center gap-2"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  {auth ? "Enter Dashboard" : "Create Account"}
                  <FiChevronRight size={18} />
                </>
              )}
            </button>

            {errorMessage && (
              <div className="p-4 bg-red-50 rounded-xl border border-red-100 text-red-600 text-[10px] font-black uppercase text-center tracking-wider animate-shake">
                {errorMessage}
              </div>
            )}
          </form>

          {!auth && (
            <p className="mt-8 text-center text-[9px] font-bold text-gray-400 uppercase leading-loose">
              By joining, you agree to our{" "}
              <span onClick={() => navigate("/terms&condition")} className="text-orange-600 cursor-pointer underline">Terms</span> &{" "}
              <span onClick={() => navigate("/privatepolicy")} className="text-orange-600 cursor-pointer underline">Privacy Policy</span>
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Auth;